import { Controller } from '@hotwired/stimulus'

import $ from 'jquery';
import Litepicker from 'litepicker';
import moment from 'moment';
import { debounce, forEach, includes } from 'lodash';

import { closeFlashAlert, flashAlert } from '../../shared/flash'

export default class extends Controller {
  static targets = [
    'container',
    'continueContainer',
    'continue',
    'startDate',
    'endDate',
    'quantity',
    'quantityContainer',
    'productName',
    'productSelect',
    'productScheduleContainer',
    'productScheduleContent',
    'dateContainer',
    'scheduleLoading',
    'selectSchedule',
    'productScheduleContentDaily',
    'productScheduleContentPartial',
    'productScheduleContentHourly',
    'productScheduleContentOther',
  ];

  initialize() {
    this.changeQuantity = debounce(this.changeQuantity, 300).bind(this)
    this.pageRequestSize = 25
  }

  connect() {
    this.orderId = this.containerTarget.dataset.orderId;
    this.destinationId = this.containerTarget.dataset.destinationId;
    this.resetForm()
    this.enableDateSelectors()

    document.addEventListener('destinationSelect', this.onDestinationSelect);
    document.addEventListener('productSelect', this.onProductSelect);
    document.addEventListener('positionSelected', this.onChoosePosition);
    document.addEventListener('orderSubmitSuccess', this.onOrderSuccess);
    document.addEventListener('cartAddSuccess', this.onCartAddSuccess);
  }

  resetForm() {
    closeFlashAlert()
    this.destinationId = this.containerTarget.dataset.destinationId;
    this.quantity = 1;
    this.selectedAvailabilities = [];
    this.groupedAvails = {};
    this.currentSelectionGroup = '';
    this.resetAvailabilities()

    if (this.litePicker) {
      this.litePicker.clearSelection()
    }
  }

  onCartAddSuccess = event => {
    this.resetForm();
    this.destinationId = event?.detail?.destinationId
  }

  enableLoading() {
    this.selectScheduleTarget.classList.add('hidden');
    this.startDateTarget.setAttribute('disabled', true);
    this.endDateTarget.setAttribute('disabled', true);
    this.scheduleLoadingTarget.classList.remove('hidden');
    this.productScheduleContentTarget.classList.add('hidden');
    this.quantityTarget.setAttribute('readonly', true);
  }

  stopLoading() {
    this.selectScheduleTarget.classList.remove('hidden');
    this.startDateTarget.removeAttribute('disabled');
    this.endDateTarget.removeAttribute('disabled');
    this.quantityTarget.removeAttribute('readonly');
    this.productScheduleContentTarget.classList.remove('hidden');
    this.scheduleLoadingTarget.classList.add('hidden');
  }

  showDateInput(bool) {
    if (bool) {
      this.dateContainerTarget.classList.remove('hidden')
    } else {
      this.dateContainerTarget.classList.add('hidden')
    }
  }

  showQuantityInput(bool) {
    if (bool) {
      this.quantityContainerTarget.classList.remove('hidden')
    } else {
      this.quantityContainerTarget.classList.add('hidden')
    }
  }

  showContinueBtn(bool) {
    if (bool) {
      this.continueContainerTarget.classList.remove('hidden')
    } else {
      this.continueContainerTarget.classList.add('hidden')
    }
  }

  enableContinueBtn(bool) {
    if (bool) {
      this.continueTarget.removeAttribute('disabled')
    } else {
      this.continueTarget.setAttribute('disabled', true)
    }
  }

  setInputVisibility = () => {
    let showDate = false
    let showQuantity
    let showContinue = false

    if (this.product.is_rental) {
      showDate = true
      showQuantity = true

      if (this.product.layout) {
        showQuantity = false
        showContinue = true
      }
    } else {
      showQuantity = true
    }

    this.showContinueBtn(showContinue)
    this.showQuantityInput(showQuantity)
    this.showDateInput(showDate)
  }

  isInputValid = () => {
    if (!this.product.is_rental) {
      return !!this.quantity
    } else if (this.product.layout) {
      return !!this.selectedAvailabilities.length
    } else {
      return !!this.quantity && !!this.selectedAvailabilities.length
    }
  }

  dispatchContinueEvent = () => {
    this.continueTarget.setAttribute('disabled', true)

    const path = '/sandbar/admin/available_inventory'
    let recurrence_ids = Array.from(new Set(this.selectedAvailabilities.map(a => a.recurrence_id))) // Set used for uniqueness
    let recurrence_arg = ''
    if (recurrence_ids.length > 0) {
      recurrence_arg = recurrence_ids.map(recurrence_id => `&recurrence_id[]=${recurrence_id}`).join('')
    }
    fetch(
      `${path}?start_date=${this.startDate}&end_date=${this.endDate}&destination_id=${this.destinationId}\
      &product_id=${this.product.id}${recurrence_arg}`,
      {
        method: 'GET',
        headers: this.fetchHeaders,
      }
    )
      .then(resp => {
        this.stopLoading()
        return resp.json()
      })
      .then(inventory => {
        const detail = {
          startDate: this.startDate,
          endDate: this.endDate,
          availabilities: this.selectedAvailabilities,
          availabilityIds: this.selectedAvailabilities.map(a => a.id),
          groupedAvails: this.groupedAvails,
          selectedGroupedAvails: this.selectedGroupedAvails,
          inventory_unit_ids_available_for_all_availabilities: inventory.map(inventory_unit => inventory_unit.id),
        }
        const quantitySelected = new CustomEvent('scheduleContinue', { detail })
        document.dispatchEvent(quantitySelected)
      })
      .catch(err => this.onHandleError(err));

  }

  dispatchQuantityEvent = () => {
    const detail = {
      quantity: this.quantityTarget.value
    }
    const quantitySelected = new CustomEvent('quantitySelected', { detail })
    document.dispatchEvent(quantitySelected)
  }

  dispatchAvailabilityEvent = () => {
    const detail = {
      endDate: this.endDate,
      startDate: this.startDate,
      quantity: this.quantity,
      availabilities: this.selectedAvailabilities,
      availabilityIds: this.selectedAvailabilities.map(a => a.id),
      groupedAvails: this.groupedAvails,
      selectedGroupedAvails: this.selectedGroupedAvails,
    }
    const availabilitySelected = new CustomEvent('availabilitySelected', { detail })
    document.dispatchEvent(availabilitySelected)
  }

  formUpdate = () => {
    if (this.isInputValid()) {
      this.enableContinueBtn(true)
      if (!this.product.layout) {
        this.dispatchAvailabilityEvent()
      } else {
        this.enableContinueBtn(true)
      }
    } else {
      this.this.enableContinueBtn(true)
    }
  }

  onProductSelect = event => {
    this.resetForm()
    this.product = event?.detail?.product
    this.productId = this.product?.id
    this.destinationId = event?.detail?.destinationId
    this.productNameTarget.innerText = this.product?.name || 'Select Schedule'

    this.availabilities = []
    this.groupedAvailabilities = {}
    this.availabilityIds = []

    this.setInputVisibility()
    if (!this.product.is_rental && this.isInputValid()) {
      this.dispatchQuantityEvent()
    }
  }

  onDestinationSelect = event => {
    this.containerTarget.dataset.destinationId = event?.detail?.destinationId
    this.resetForm()
  }

  changeQuantity = _event => {
    closeFlashAlert();
    const quantity = parseInt(this.quantityTarget.value);

    if (quantity > 10) {
      flashAlert('Please select fewer than 10 units at a time.')
      return
    } else if (quantity < 1) {
      flashAlert('Please select at least one unit.')
      return
    }

    this.quantity = quantity;
    this.clearAvailabilitySelection();

    if (this.product.is_rental && !this.product.layout) {
      this.findProductAvailabilities();
    }

    this.dispatchQuantityEvent()
  }

  clearQuantitySelection() {
    this.quantityTarget.value = 1;
  }

  clearDateSelectors() {
    if (this.litePicker) {
      this.litePicker.clearSelection()
    }
  }

  showDateSelectors = _event => {
    if (!this.litePicker) {
      this.enableDateSelectors();
    }

    this.litePicker.show();
  }

  enableDateSelectors = () => {
    const options = {
      resetButton: true,
      element: this.startDateTarget,
      tooltipText: { one: 'day', other: 'days' },
      singleMode: false,
      elementEnd: this.endDateTarget,
      format: 'MM/DD/YYYY',
      maxDays: 30,
      numberOfMonths: 2,
      numberOfColumns: 2,
      switchingMonths: 1,
      allowRepick: true,
      setup: picker => {
        picker.on('hide', () => {
          this.onDatePickerHide();
        })
        picker.on('selected', (_date1, _date2) => {
          picker.hide();
        });
      },
    };

    this.litePicker = new Litepicker(options);
  }

  onDatePickerHide = () => {
    this.startDate = this.startDateTarget.value ? new Date(this.startDateTarget.value).toUTCString() : null
    this.endDate = this.endDateTarget.value ? new Date(this.endDateTarget.value).toUTCString() : null
    if (this.startDate && this.endDate) {
      this.findProductAvailabilities();
    } else {
      this.resetForm()
    }
  }

  resetAvailabilities() {
    this.productScheduleContainerTarget.classList.add('hidden');
    this.productScheduleContentTarget.classList.add('hidden');
    this.productScheduleContentOtherTarget.classList.add('hidden')
    this.productScheduleContentHourlyTarget.classList.add('hidden')
    this.productScheduleContentPartialTarget.classList.add('hidden')
    this.productScheduleContentDailyTarget.classList.add('hidden')
    $('.js-availability').remove();
    this.displayedAvails = 0;
    this.displayedKeys = {};
  }

  displayAvails() {
    this.resetAvailabilities()
    this.productScheduleContainerTarget.classList.remove('hidden');
    this.productScheduleContentTarget.classList.remove('hidden');
    let keyTargetMap = {
      'Daily Selection': this.productScheduleContentDailyTarget,
      'Hourly Selection': this.productScheduleContentHourlyTarget,
      'Partial Day Selection': this.productScheduleContentPartialTarget,
      Other: this.productScheduleContentOtherTarget,
    }

    Object.keys(this.groupedAvails).forEach(avail => {
      if (!this.displayedKeys[this.groupedAvails[avail][0].key]) {
        keyTargetMap[this.groupedAvails[avail][0].key].classList.remove('hidden');
        this.displayedKeys[this.groupedAvails[avail][0].key] = true;
      }
      keyTargetMap[this.groupedAvails[avail][0].key].insertAdjacentHTML('beforeend', this.buildAvail(this.groupedAvails[avail][0]));
      this.displayedAvails += 1;
    });

    if (this.displayedAvails === 0) {
      this.continueContainerTarget.setAttribute('disabled', true);
      flashAlert('There aren’t enough availabilities for all of your selected dates. Please select fewer dates.')
      this.productScheduleContentTarget.classList.add('hidden');
    }
  }

  removeFromSelected(_availability, category) {
    this.selectedAvailabilities = this.selectedAvailabilities.filter(avail => {
      return category !== avail.group_key;
    })
    if (this.selectedAvailabilities.length === 0) {
      $('.js-availability').removeAttr('disabled');
    }
  }

  clearAllSelections() {
    this.selectedAvailabilities = [];
    $('.js-availability').removeClass('selected');
    $('.js-availability').removeAttr('disabled');
  }

  disableIncompatibleAvails(currentAvailability) {
    let self = this;
    if (currentAvailability.is_all_day) {
      $('.js-all-day-false').attr('disabled', true);
      $('.js-all-day-true').each(function (_i, availabilitySelect) {
        if ($(availabilitySelect).data('availability-title') !== currentAvailability.title) {
          $(availabilitySelect).attr('disabled', true);
        }
      });
    } else {
      $('.js-all-day-true').attr('disabled', true);
      $('.js-all-day-false').each(function (_i, availabilitySelect) {
        if ($(availabilitySelect).data('availability-category') === currentAvailability.group_key) {
          return;
        }
        if (self.overlaps(self.groupedAvails[$(availabilitySelect).data('availability-category')][0], currentAvailability)) {
          $(availabilitySelect).attr('disabled', true);
        }
      });
    }
  }

  overlaps(firstAvail, secondAvail) {
    if (firstAvail.starts_at <= secondAvail.starts_at && secondAvail.starts_at < firstAvail.ends_at) {
      return true;
    }

    if (firstAvail.starts_at < secondAvail.ends_at && secondAvail.ends_at <= firstAvail.ends_at) {
      return true;
    }

    if (secondAvail.starts_at < firstAvail.starts_at && firstAvail.ends_at < secondAvail.ends_at) {
      return true;
    }

    return false;
  }

  displayCurrentAvailabilityStates = () => {
    let self = this;
    $('.js-availability').removeAttr('disabled');
    $.each(this.selectedAvailabilities, function (_i, selectedAvail) {
      self.disableIncompatibleAvails(selectedAvail);
    });
  }

  chooseAvailability = event => {
    const element = event.currentTarget;
    const currentAvailabilityId = element.dataset.availabilityId;
    const currentAvailabilityCategory = element.dataset.availabilityCategory;

    if (this.groupedAvails[currentAvailabilityCategory][0].is_all_day !== this.currentSelectionGroup) {
      this.clearAllSelections();
    }
    this.currentSelectionGroup = this.groupedAvails[currentAvailabilityCategory][0].is_all_day;

    if (element.classList.contains('selected')) {
      element.classList.remove('selected');
      this.removeFromSelected(currentAvailabilityId, currentAvailabilityCategory);

    } else {
      element.classList.add('selected');
      this.selectedAvailabilities = this.selectedAvailabilities.concat(this.groupedAvails[currentAvailabilityCategory]);
    }

    let selectedGroupedAvails = [];
    let selectedAvailIds = this.selectedAvailabilities.map(a => a.id)

    forEach(this.groupedAvails, avail => {
      if (includes(selectedAvailIds, avail[0].id)) {
        selectedGroupedAvails.push(avail[0]);
      }
    })

    this.displayCurrentAvailabilityStates();
    this.dispatchAvailabilityEvent();

    this.formUpdate();
  }

  continue(event) {
    event.preventDefault();
    let selectedGroupedAvails = [];
    let selectedAvailIds = this.selectedAvailabilities.map(a => a.id)

    forEach(this.groupedAvails, avail => {
      if (includes(selectedAvailIds, avail[0].id)) {
        selectedGroupedAvails.push(avail[0]);
      }
    })

    this.dispatchContinueEvent()
  }

  changeSchedule(event) {
    event.preventDefault();
    this.updateQuantity();
    this.clearAllSelections();
  }

  buildLabel(avail) {
    return `<label class="block mb-2">${avail}</a>`;
  }

  buildAvail(availDetails) {
    return `<a class="btn btn-hollow inline-block mb-2 mr-4 js-availability js-all-day-${availDetails.is_all_day}"
      data-action="click->order--schedule#chooseAvailability"
      data-availability-category="${availDetails.group_key}"
      data-availability-id="${availDetails.id}"
      data-availability-title="${availDetails.title}"
      data-start="${availDetails.starts_at}"
      data-end="${availDetails.ends_at}">${availDetails.title}</a>`;
  }

  getKeyName(startsAt, endsAt) {
    const durationInSeconds = moment.utc(endsAt).diff(moment.utc(startsAt), 'seconds');

    if (durationInSeconds >= 86340 && durationInSeconds <= 86400) {
      return 'Daily Selection';
    }

    if (durationInSeconds > 3600 && durationInSeconds < 86340) {
      return 'Partial Day Selection';
    }

    if (durationInSeconds === 3600) {
      return 'Hourly Selection';
    }

    return 'Other';
  }

  groupBy(objectArray) {
    return objectArray.reduce((acc, obj) => {
      let keyName = this.getKeyName(obj.starts_at, obj.ends_at);
      if (!acc.keyName) {
        acc[keyName] = []
      }
      obj.key = keyName;
      acc[keyName].push(obj)
      return acc
    }, {})
  }

  groupByCategory(objectArray) {
    return objectArray.reduce((acc, obj) => {
      let key = obj.group_key;
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(obj)
      return acc
    }, {})
  }

  clearAvailabilitySelection() {
    this.selectedAvailabilities = [];
  }

  findProductAvailabilities() {
    if (!this.startDate || !this.endDate || !this.destinationId) {
      return;
    }
    this.enableLoading();
    this.clearAvailabilitySelection()

    const path = '/sandbar/admin/availabilities'
    fetch(`${path}?product_id=${this.productId}&destination_id=${this.destinationId}&start_date=${this.startDate}&end_date=${this.endDate}`)
      .then(resp => {
        if (resp.ok) {
          return resp.json();
        } else {
          resp.json().then(err => this.handleError(err))
        }
      })
      .then(availabilities => {
        this.groupedAvails = this.groupByCategory(availabilities);
        this.displayAvails(this.groupBy(availabilities));
        this.stopLoading();
      })
      .catch(err => this.handleError(err));
  }

  handleError(err) {
    console.error(err)
  }
}
